import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { IconMapPin, IconSearch } from '@tabler/icons-react';
import { createPortal } from 'react-dom';
import {
  fetchUserLocationSettings,
  getLocationName,
  resolveCityLocationId,
  upsertUserLocationSetting,
} from '../api/locationApi';
import { fetchMyAppUserRow } from '../api/profileApi';
import { REGION_CITY_ROWS } from '../data/regionsCatalog';
import { useMingo } from '../context/MingoContext';
import { formatRegionLabel } from '../lib/formatRegionLabel';
import { formatStoredRegionLabel } from '../lib/regionDisplayLabel';
import { getSupabase, isSupabaseConfigured } from '../lib/supabase';
import { resetMobileViewportZoom } from '../lib/resetMobileViewport';
import { formatLocationDistrictLabel } from '../lib/regionDisplayLabel';
import { flagEmojiForSelectedRegion, flagEmojiFromCountryCode } from '../utils/regionFlagEmoji';
import { locationValueFromDistrictPick } from '../lib/regionSlots';
import { useIsDesktop } from '../lib/deviceType';
import { RegionStateDebugBox } from './RegionStateDebugBox';
import { geocodeAddress, haversineDistanceKm } from '../lib/geoUtils';
import { findNearestLocation } from '../api/locationApi';
import PageHeaderWithBack from './PageHeaderWithBack';

const SHEET_BODY_STYLE = { maxHeight: 'none', overflow: 'visible' };
const BACKDROP_CLICK_GUARD_MS = 450;
const PANEL_STYLE = { maxHeight: 'none', overflow: 'visible' };
const RESULT_LIST_STYLE = { display: 'flex', flexDirection: 'column', gap: 8, marginTop: 10 };

const SLOT_RADIUS_OPTIONS = [
  { km: 1, label: '1km', desc: '' },
  { km: 3, label: '3km', desc: '' },
  { km: 5, label: '5km', desc: '' },
  { km: 10, label: '10km', desc: '' },
  { km: 20, label: '20km', desc: '' },
  { km: 50, label: '50km', desc: '' },
];

const SEARCH_DEBOUNCE_MS = 300;
const SEARCH_LIMIT = 20;
/** DB fetch cap before client dedupe (city-level grouping) */
const SEARCH_FETCH_LIMIT = 50;

const DISTRICT_SEARCH_COLUMNS = [
  'city_ko',
  'city_en',
  'city_ru',
  'city_vi',
  'district_ko',
  'district_en',
  'district_ru',
  'district_vi',
  'country_name_ko',
  'country_name_en',
  'country_name_ru',
  'country_name_vi',
  /** 펼친 별칭 문자열 (search_aliases TEXT[] → SQL Editor migration) */
  'search_aliases_flat',
];

/**
 * @param {string} s
 */
function escapeIlike(s) {
  return String(s || '').replace(/[%_\\]/g, '\\$&');
}

/**
 * @param {string} s
 */
function normalizeSearchQuery(s) {
  return String(s || '').trim().normalize('NFC');
}

/**
 * @param {string} query
 * @returns {string[]}
 */
function splitSearchTokens(query) {
  const raw = normalizeSearchQuery(query);
  if (!raw) return [];
  return raw
    .split(/\s+/)
    .map((x) => x.trim())
    .filter(Boolean);
}

/**
 * PostgREST .or() — wrap %pattern% in quotes (reserved char safety).
 * @param {string} needle
 */
function ilikePatternForOr(needle) {
  const pattern = `%${escapeIlike(needle)}%`;
  return `"${pattern.replace(/"/g, '""')}"`;
}

/**
 * @param {object} row
 * @returns {string}
 */
function searchableText(row) {
  return [
    row.country_name_ko,
    row.country_name_en,
    row.country_name_ru,
    row.country_name_vi,
    row.city_ko,
    row.city_en,
    row.city_ru,
    row.city_vi,
    row.district_ko,
    row.district_en,
    row.district_ru,
    row.district_vi,
    row.search_aliases_flat,
  ]
    .map((v) => String(v || '').toLowerCase())
    .join(' ');
}

/**
 * @param {object} row
 * @param {string[]} tokens normalized lower-case
 */
function rowMatchesAllTokens(row, tokens) {
  if (!tokens.length) return true;
  const text = searchableText(row);
  return tokens.every((tk) => text.includes(tk));
}

/**
 * @param {string} a
 * @param {string} b
 */
function normLoc(a, b) {
  return String(a || '').trim().toLowerCase() === String(b || '').trim().toLowerCase();
}

/**
 * @param {{ city_ko?: string; city_en?: string; district_ko?: string; district_en?: string }} row
 */
function isDistinctDistrict(row) {
  const city = row.city_en || row.city_ko || '';
  const district = row.district_en || row.district_ko || '';
  return Boolean(district && city && !normLoc(district, city));
}

/**
 * @param {object} row
 * @param {string} lang
 * @param {'city' | 'district'} mode
 */
function formatDistrictResultLabel(row, lang, mode) {
  return formatLocationDistrictLabel(row, lang, mode);
}

/**
 * Lower = higher in search results.
 * @param {object} row
 * @param {string} q trimmed lower query
 * @param {'city'|'district'} mode
 */
function districtSearchRank(row, q, mode) {
  const compactQ = q.replace(/\s+/g, '');
  const cityKo = String(row.city_ko || '').trim().toLowerCase();
  const cityEn = String(row.city_en || '').trim().toLowerCase();
  const cityRu = String(row.city_ru || '').trim().toLowerCase();
  const cityVi = String(row.city_vi || '').trim().toLowerCase();
  const distKo = String(row.district_ko || '').trim().toLowerCase();
  const distEn = String(row.district_en || '').trim().toLowerCase();
  const distRu = String(row.district_ru || '').trim().toLowerCase();
  const distVi = String(row.district_vi || '').trim().toLowerCase();
  const countryKo = String(row.country_name_ko || '').trim().toLowerCase();
  const countryEn = String(row.country_name_en || '').trim().toLowerCase();
  const countryRu = String(row.country_name_ru || '').trim().toLowerCase();
  const countryVi = String(row.country_name_vi || '').trim().toLowerCase();
  const fullKo = `${countryKo} ${cityKo} ${distKo}`.trim();
  const fullEn = `${countryEn} ${cityEn} ${distEn}`.trim();
  const fullRu = `${countryRu} ${cityRu} ${distRu}`.trim();
  const fullVi = `${countryVi} ${cityVi} ${distVi}`.trim();
  const compactKo = fullKo.replace(/\s+/g, '');
  const compactEn = fullEn.replace(/\s+/g, '');
  const compactRu = fullRu.replace(/\s+/g, '');
  const compactVi = fullVi.replace(/\s+/g, '');

  const cityExact = cityKo === q || cityEn === q || cityRu === q || cityVi === q;
  const distExact = distKo === q || distEn === q || distRu === q || distVi === q;
  const fullExact = fullKo === q || fullEn === q || fullRu === q || fullVi === q;
  const compactExact =
    compactQ && (compactKo === compactQ || compactEn === compactQ || compactRu === compactQ || compactVi === compactQ);

  // "나주시"를 검색했을 때 "전남광주특별시(구가 나주시인 같은 행의 city 전용 항목)"가
  // district 전용 매치와 같은 순위(0)로 묶여 위로 뜨던 버그 수정:
  // mode='city' 카드는 오직 city 자체 필드가 맞아떨어질 때만 최상위로 올린다.
  if (mode === 'city') {
    if (cityExact || fullExact || compactExact) return 0;
    if (cityKo.includes(q) || cityEn.includes(q) || cityRu.includes(q) || cityVi.includes(q)) return 1;
    // city 필드는 전혀 안 맞고 district 필드 때문에 매치된 경우 (예: "나주시" 검색 시 상위 도시행) → 최하위로
    return 6;
  }

  if (distExact || fullExact || compactExact) return 0;
  if (cityKo.includes(q) || cityEn.includes(q) || cityRu.includes(q) || cityVi.includes(q)) return 2;
  if (distKo.includes(q) || distEn.includes(q) || distRu.includes(q) || distVi.includes(q)) return 3;
  return 4;
}

/**
 * @param {object[]} rows
 * @param {string} q trimmed lower query
 * @param {'city'|'district'} mode
 */
function sortDistrictRowsBySearchRelevance(rows, q, mode) {
  return [...rows].sort((a, b) => districtSearchRank(a, q, mode) - districtSearchRank(b, q, mode));
}

/**
 * @param {object[]} rows
 * @param {string} query
 * @param {string} lang
 */
function buildSearchDisplayItems(rows, query, lang) {
  const q = normalizeSearchQuery(query).toLowerCase();
  if (!q || !rows.length) return [];
  const tokens = splitSearchTokens(q).map((x) => x.toLowerCase());
  const matchedRows = rows.filter((r) => rowMatchesAllTokens(r, tokens));
  if (!matchedRows.length) return [];

  const sortedCityRows = sortDistrictRowsBySearchRelevance(matchedRows, q, 'city');
  const sortedDistrictRows = sortDistrictRowsBySearchRelevance(matchedRows, q, 'district');

  /** @type {Array<{ row: object; label: string; mode: 'city' | 'district' }>} */
  const items = [];
  const seen = new Set();

  const push = (row, mode) => {
    const key =
      mode === 'city'
        ? `city:${row.country_code}:${row.city_en}:${row.city_ko}`
        : `dist:${row.id ?? ''}:${row.city_en}:${row.district_en}`;
    if (seen.has(key)) return;
    seen.add(key);
    items.push({ row, mode, label: formatDistrictResultLabel(row, lang, mode) });
  };

  const cityKeys = new Set();
  for (const row of sortedCityRows) {
    const ck = `${row.country_code}:${row.city_en}`;
    if (cityKeys.has(ck)) continue;
    cityKeys.add(ck);
    push(row, 'city');
    if (items.length >= SEARCH_LIMIT) break;
  }

  for (const row of sortedDistrictRows) {
    if (!isDistinctDistrict(row)) continue;
    push(row, 'district');
    if (items.length >= SEARCH_LIMIT) break;
  }

  if (items.length === 0) {
    for (const row of sortedDistrictRows) {
      push(row, isDistinctDistrict(row) ? 'district' : 'city');
      if (items.length >= SEARCH_LIMIT) break;
    }
  }

  items.sort((a, b) => districtSearchRank(a.row, q, a.mode) - districtSearchRank(b.row, q, b.mode));

  return items.slice(0, SEARCH_LIMIT);
}

/**
 * Korean catalog label → English pick (e.g. 런던 → London) when DB city_ko not translated yet.
 * @param {string} query
 * @returns {string[]}
 */
function expandDistrictSearchNeedles(query) {
  const raw = normalizeSearchQuery(query);
  if (!raw) return [];
  const needles = new Set([raw]);
  const lower = raw.toLowerCase();

  for (const row of REGION_CITY_ROWS) {
    const ko = (row.ko || '').trim();
    const pick = (row.pick || '').trim();
    if (!pick) continue;
    if (ko && (ko === raw || ko.toLowerCase() === lower)) {
      needles.add(pick);
    }
    if (pick.toLowerCase() === lower || pick.toLowerCase().includes(lower)) {
      needles.add(pick);
    }
  }

  return [...needles];
}

/**
 * @param {string[]} needles
 */
function buildDistrictsOrFilter(needles) {
  const parts = [];
  for (const needle of needles) {
    const pat = ilikePatternForOr(needle);
    for (const col of DISTRICT_SEARCH_COLUMNS) {
      parts.push(`${col}.ilike.${pat}`);
    }
  }
  return parts.join(',');
}

const DISTRICTS_SELECT =
  'id, location_id, country_code, country_name_ko, country_name_en, country_name_ru, country_name_vi, city_ko, city_en, city_ru, city_vi, district_ko, district_en, district_ru, district_vi, search_aliases_flat, lat, lng';

const ADDRESS_MAX_DIST_KM = 30;

/**
 * Map geocoded lat/lng to nearest districts row (prefer real district over city-only).
 * @param {number} lat
 * @param {number} lng
 * @returns {Promise<object|null>}
 */
async function findNearestDistrictRow(lat, lng) {
  if (!isSupabaseConfigured) return null;
  const sb = getSupabase();
  if (!sb) return null;

  // Prefer locations.level=district when available, then join districts via location_id
  const nearestLoc = await findNearestLocation(lat, lng, 'district', ADDRESS_MAX_DIST_KM);
  if (nearestLoc?.id) {
    const { data, error } = await sb
      .from('districts')
      .select(DISTRICTS_SELECT)
      .eq('location_id', nearestLoc.id)
      .limit(1)
      .maybeSingle();
    if (!error && data) return data;
  }

  // Fallback: scan districts with coordinates
  const { data: rows, error } = await sb
    .from('districts')
    .select(DISTRICTS_SELECT)
    .not('lat', 'is', null)
    .not('lng', 'is', null)
    .limit(800);
  if (error || !rows?.length) return null;

  let best = null;
  let bestKm = Infinity;
  for (const row of rows) {
    if (!isDistinctDistrict(row)) continue;
    const d = haversineDistanceKm(lat, lng, row.lat, row.lng);
    if (d < bestKm) {
      bestKm = d;
      best = row;
    }
  }
  if (!best || bestKm > ADDRESS_MAX_DIST_KM) return null;
  return best;
}

/**
 * Per-column .ilike (reliable UTF-8 in browser; avoids long .or() filter encoding issues).
 * @param {import('@supabase/supabase-js').SupabaseClient} sb
 * @param {string} needle
 */
async function fetchDistrictsByIlikeColumns(sb, needle) {
  const pat = `%${escapeIlike(needle)}%`;
  const byId = new Map();

  const responses = await Promise.all(
    DISTRICT_SEARCH_COLUMNS.map((col) =>
      sb.from('districts').select(DISTRICTS_SELECT).ilike(col, pat).limit(SEARCH_FETCH_LIMIT),
    ),
  );

  for (let i = 0; i < responses.length; i += 1) {
    const { data, error } = responses[i];
    const col = DISTRICT_SEARCH_COLUMNS[i];
    if (error) {
      // eslint-disable-next-line no-console
      console.warn(`[LocationBottomSheet] districts ilike ${col}`, error.message, { needle, pat });
      continue;
    }
    for (const row of data || []) {
      if (row?.id) byId.set(row.id, row);
    }
  }

  return [...byId.values()];
}

/**
 * @param {string} query
 */
async function searchDistrictsTable(query) {
  const raw = normalizeSearchQuery(query);
  const tokens = splitSearchTokens(raw);
  if (!tokens.length || !isSupabaseConfigured) {
    // eslint-disable-next-line no-console
    console.log('[LocationBottomSheet] districts search skipped', {
      query: raw,
      configured: isSupabaseConfigured,
    });
    return [];
  }

  const sb = getSupabase();
  if (!sb) return [];

  const tokenNeedles = tokens.map((tk) => expandDistrictSearchNeedles(tk));
  const needles = [...new Set(tokenNeedles.flat())];
  const orFilter = buildDistrictsOrFilter(needles);
  const patSample = `%${escapeIlike(raw)}%`;

  const searchData = {
    query: raw,
    tokens,
    needles,
    city_ko_ilike: patSample,
    orFilterPreview: orFilter.length > 200 ? `${orFilter.slice(0, 200)}…` : orFilter,
    columns: DISTRICT_SEARCH_COLUMNS,
  };
  // eslint-disable-next-line no-console
  console.log('[LocationBottomSheet] districts search query', JSON.stringify(searchData));

  /** @type {Map<string, object>} */
  const byId = new Map();
  const addRows = (rows) => {
    for (const row of rows) {
      if (row?.id) byId.set(row.id, row);
    }
  };

  // AND semantics for multi-word queries:
  // each token must match at least one searchable column.
  let intersected = null;
  for (const tokenSet of tokenNeedles) {
    const tokenMap = new Map();
    for (const needle of tokenSet) {
      const rows = await fetchDistrictsByIlikeColumns(sb, needle);
      for (const row of rows) {
        if (row?.id) tokenMap.set(row.id, row);
      }
    }
    if (!intersected) {
      intersected = tokenMap;
    } else {
      const next = new Map();
      for (const [id, row] of intersected.entries()) {
        if (tokenMap.has(id)) next.set(id, tokenMap.get(id) || row);
      }
      intersected = next;
    }
    if (!intersected.size) break;
  }

  addRows([...(intersected?.values() || [])]);
  const merged = [...byId.values()].slice(0, SEARCH_FETCH_LIMIT);

  const sample = merged[0]
    ? {
        city_ko: merged[0].city_ko,
        city_en: merged[0].city_en,
        country_code: merged[0].country_code,
      }
    : null;
  const rowCount = merged.length;
  // eslint-disable-next-line no-console
  console.log('[LocationBottomSheet] districts search result', JSON.stringify({ rowCount, sample }));

  return merged;
}

/**
 * @param {{
 *   open: boolean;
 *   onClose: () => void;
 *   initialSlot?: 0 | 1;
 *   writePickMode?: boolean;
 *   onWritePick?: (label: string, row?: object | null) => void;
 *   pickTitleKey?: string;
 *   presentation?: 'sheet' | 'page';
 *   promptLabelOnConfirm?: boolean;
 *   defaultLabel?: string;
 *   aboveBody?: React.ReactNode;
 *   belowBody?: React.ReactNode;
 *   pageHeaderRightSlot?: React.ReactNode;
 * }} props
 */
function LocationBottomSheet({
  open,
  onClose,
  initialSlot = 0,
  writePickMode = false,
  onWritePick,
  pickTitleKey = 'write.regionPickTitle',
  presentation = 'sheet',
  promptLabelOnConfirm = false,
  defaultLabel = '',
  aboveBody = null,
  belowBody = null,
  pageHeaderRightSlot = null,
}) {
  const {
    t,
    language,
    syncSelectedRegions,
    setActiveRegionSlot,
    gpsSlotLabel,
    setGpsSlot,
    pickSlot,
    pickSlotLabel,
    setPickSlot,
    setActiveSource,
    currentLocation,
    userCoords,
    searchRadius,
    setSearchRadius,
    isLoggedIn,
    showToast,
    gpsUnmapped,
    clearGpsUnmapped,
  } = useMingo();

  const isDesktop = useIsDesktop();
  const isPage = presentation === 'page';
  const [gpsManualStep, setGpsManualStep] = useState('search');

  const [entered, setEntered] = useState(false);
  const [appUser, setAppUser] = useState(null);
  const [primaryRadiusKm, setPrimaryRadiusKm] = useState(searchRadius);
  const [secondaryRadiusKm, setSecondaryRadiusKm] = useState(searchRadius);
  const [secondaryStep, setSecondaryStep] = useState('search');
  const [districtQuery, setDistrictQuery] = useState('');
  const [districtResults, setDistrictResults] = useState([]);
  const [districtSearching, setDistrictSearching] = useState(false);
  const [selectedDistrictRow, setSelectedDistrictRow] = useState(null);
  const [secondaryDisplayLabel, setSecondaryDisplayLabel] = useState('');
  const [confirmLabel, setConfirmLabel] = useState('');
  const [manualCountry, setManualCountry] = useState('');
  const [manualCity, setManualCity] = useState('');
  const [saving, setSaving] = useState(false);
  const searchSeqRef = useRef(0);
  const openedAtRef = useRef(0);

  const isTrackGps = !writePickMode && initialSlot === 0;
  const isTrackManual = writePickMode || initialSlot === 1;
  const isGpsManualPick = isTrackGps && isDesktop && gpsUnmapped;

  const primaryCity = gpsSlotLabel || 'Da Nang';

  const gpsCityName = useMemo(() => {
    const raw =
      gpsSlotLabel ||
      (currentLocation ? getLocationName(currentLocation, language) : '') ||
      primaryCity;
    return formatStoredRegionLabel(raw, t, language) || formatRegionLabel(primaryCity, t, language);
  }, [gpsSlotLabel, currentLocation, language, primaryCity, t]);

  const interestDisplayLabel = useMemo(() => {
    const raw = pickSlotLabel || pickSlot?.city_label || '';
    return formatStoredRegionLabel(raw, t, language);
  }, [pickSlotLabel, pickSlot, t, language]);

  const gpsCountryCode = currentLocation?.country_code || null;

  const resetSecondaryManual = useCallback(() => {
    setDistrictQuery('');
    setDistrictResults([]);
    setDistrictSearching(false);
    setSelectedDistrictRow(null);
    setSecondaryDisplayLabel('');
    setConfirmLabel('');
    setManualCountry('');
    setManualCity('');
    setSecondaryStep('search');
  }, []);

  const loadSettings = useCallback(async () => {
    if (!isLoggedIn || !isSupabaseConfigured) {
      setAppUser(null);
      return;
    }
    const user = await fetchMyAppUserRow();
    setAppUser(user);
    if (user?.id) {
      const rows = await fetchUserLocationSettings(user.id);
      const primarySetting = rows.find((s) => s.is_primary) || rows[0];
      const secondarySetting =
        rows.find((s) => !s.is_primary && s.location_id !== primarySetting?.location_id) || rows[1];
      if (primarySetting?.radius_km) setPrimaryRadiusKm(primarySetting.radius_km);
      else setPrimaryRadiusKm(searchRadius);
      if (secondarySetting?.radius_km) setSecondaryRadiusKm(secondarySetting.radius_km);
      else setSecondaryRadiusKm(searchRadius);
    }
  }, [isLoggedIn, searchRadius]);

  useEffect(() => {
    if (!open) {
      setEntered(false);
      resetSecondaryManual();
      return undefined;
    }
    setActiveRegionSlot(initialSlot);
    setPrimaryRadiusKm(searchRadius);
    setSecondaryRadiusKm(searchRadius);

    if (initialSlot === 1) {
      // Open manual track from a clean slate every time.
      resetSecondaryManual();
    } else if (initialSlot === 0 && isDesktop && gpsUnmapped) {
      resetSecondaryManual();
      setGpsManualStep('search');
    }

    void loadSettings();
    openedAtRef.current = Date.now();
    const id = requestAnimationFrame(() => setEntered(true));
    return () => cancelAnimationFrame(id);
  }, [open, initialSlot, isDesktop, gpsUnmapped, loadSettings, resetSecondaryManual, searchRadius, setActiveRegionSlot]);

  useEffect(() => {
    if (!promptLabelOnConfirm) return;
    const next = String(defaultLabel || '').trim();
    if (next) setConfirmLabel(next);
  }, [defaultLabel, promptLabelOnConfirm]);

  const districtSearchOpen =
    (isTrackManual && (!writePickMode && secondaryStep === 'search')) ||
    (isGpsManualPick && gpsManualStep === 'search');

  useEffect(() => {
    if (!open || !districtSearchOpen) return undefined;

    const needle = normalizeSearchQuery(districtQuery);
    if (!needle) {
      setDistrictResults([]);
      setDistrictSearching(false);
      return undefined;
    }

    const seq = ++searchSeqRef.current;
    setDistrictSearching(true);

    const timer = window.setTimeout(() => {
      void searchDistrictsTable(needle).then((rows) => {
        if (searchSeqRef.current !== seq) return;
        setDistrictResults(buildSearchDisplayItems(rows, needle, language));
        setDistrictSearching(false);
      });
    }, SEARCH_DEBOUNCE_MS);

    return () => window.clearTimeout(timer);
  }, [districtQuery, districtSearchOpen, language, open]);

  const handleClose = useCallback(() => {
    // 1) 키보드 내림: 현재 포커스 해제
    if (typeof document !== 'undefined' && document.activeElement) {
      document.activeElement.blur();
    }
    // 2) iOS Safari viewport 복원 (keyboard / zoom)
    resetMobileViewportZoom();
    setEntered(false);
    // Keep persisted regions; only reset local manual picker flow for next open.
    resetSecondaryManual();
    setGpsManualStep('search');
    window.setTimeout(onClose, 320);
  }, [onClose, resetSecondaryManual]);

  const handleBackdropPointerUp = useCallback(
    (e) => {
      if (e.target !== e.currentTarget) return;
      if (Date.now() - openedAtRef.current < BACKDROP_CLICK_GUARD_MS) {
        e.preventDefault();
        return;
      }
      handleClose();
    },
    [handleClose],
  );

  const persistSetting = useCallback(
    async ({ km, slot, locationId, label: labelOverride, isUserSaved }) => {
      if (!isLoggedIn || !appUser?.id || !isSupabaseConfigured) {
        return { ok: true, skipped: true };
      }
      if (!locationId) {
        return { ok: true, skipped: true };
      }
      try {
        await upsertUserLocationSetting({
          userId: appUser.id,
          locationId,
          radiusKm: km,
          isPrimary: slot === 0,
          label:
            labelOverride != null && String(labelOverride).trim()
              ? String(labelOverride).trim()
              : slot === 0
                ? t('location.primaryRegion')
                : t('location.secondaryRegion'),
          // 명시적으로 true를 넘길 때만 페이로드에 포함 — 캐주얼한 반경/지역 변경(예: 허브
          // 지역바에서 그냥 다른 동네 훑어볼 때)까지 "저장된 지역" 목록에 끼어들지 않도록.
          // false를 명시적으로 넘기면 기존에 진짜 저장돼 있던 행을 되돌려버릴 수 있어서
          // isUserSaved가 true일 때만 전달.
          ...(isUserSaved ? { isUserSaved: true } : {}),
        });
        return { ok: true, skipped: false };
      } catch (err) {
        const detail = err?.message || String(err);
        console.warn('[LocationBottomSheet] persistSetting failed', detail, {
          locationId,
          slot,
          km,
          code: err?.code,
        });
        return { ok: false, skipped: false, message: detail };
      }
    },
    [appUser?.id, isLoggedIn, t],
  );

  const handleConfirmPrimary = useCallback(async () => {
    if (saving) return;
    setSaving(true);
    const km = primaryRadiusKm;
    setSearchRadius(km);

    const locationId = await resolveCityLocationId(gpsCityName, {
      countryCode: currentLocation?.country_code || manualCountry || 'VN',
      language,
      locationRow: currentLocation,
      coords: userCoords,
      fallbackLabel: primaryCity,
    });

    setGpsSlot((prev) =>
      prev
        ? {
            ...prev,
            radius_km: km,
            lat: userCoords?.lat ?? prev.lat,
            lng: userCoords?.lng ?? prev.lng,
          }
        : prev,
    );

    const settingResult = await persistSetting({ km, slot: 0, locationId });
    setSaving(false);
    if (settingResult.ok) {
      showToast(t('location.radiusUpdated'));
      handleClose();
    } else if (!settingResult.skipped) {
      showToast(t('location.settingFailedDetail', { detail: settingResult.message || 'radius' }));
    } else {
      showToast(t('location.radiusUpdated'));
      handleClose();
    }
  }, [
    handleClose,
    currentLocation,
    gpsCityName,
    language,
    persistSetting,
    primaryRadiusKm,
    userCoords,
    manualCountry,
    primaryCity,
    saving,
    setSearchRadius,
    setGpsSlot,
    showToast,
    t,
  ]);

  const selectGpsManualDistrict = useCallback((item) => {
    const { row, label } = item;
    setSelectedDistrictRow(row);
    setSecondaryDisplayLabel(label);
    setManualCountry(row.country_code || '');
    setManualCity(row.city_en || row.city_ko || '');
    setGpsManualStep('radius');
  }, []);

  const handleConfirmGpsManual = useCallback(async () => {
    if (gpsManualStep !== 'radius' || saving) return;
    const label =
      secondaryDisplayLabel ||
      (selectedDistrictRow && formatDistrictResultLabel(selectedDistrictRow, language, 'city'));
    const cityEn = manualCity || selectedDistrictRow?.city_en;
    if (!label || !cityEn) {
      showToast(t('location.settingFailedDetail', { detail: 'city' }));
      return;
    }

    setSaving(true);
    const km = primaryRadiusKm;
    setSearchRadius(km);

    const pickedCityLabel =
      selectedDistrictRow?.city_ko ||
      selectedDistrictRow?.city_en ||
      label.split(' · ').pop()?.trim() ||
      label;

    const gpsValue = await locationValueFromDistrictPick(selectedDistrictRow, pickedCityLabel, {
      radius_km: km,
      country_code: manualCountry || selectedDistrictRow?.country_code,
    });
    if (!gpsValue) {
      setSaving(false);
      showToast(t('location.settingFailedDetail', { detail: 'resolve' }));
      return;
    }

    setGpsSlot(gpsValue, { label: pickedCityLabel });
    setActiveSource('gps');
    clearGpsUnmapped();

    const syncRegions = [pickedCityLabel, pickSlotLabel].filter(Boolean);
    const syncResult = await syncSelectedRegions(syncRegions);
    if (!syncResult?.ok) {
      setSaving(false);
      showToast(t('location.settingFailedDetail', { detail: syncResult?.message || 'sync' }));
      return;
    }

    // district(구/군)를 골랐으면 그 locations.id를 저장 — city 이름으로만 재조회하면
    // 항상 도시 단위로 되돌아가버려서 구/군 선택이 저장 단계에서 사라지던 문제 수정
    const locationId =
      gpsValue.district_id ||
      gpsValue.city_id ||
      (await resolveCityLocationId(cityEn, {
        countryCode: manualCountry || selectedDistrictRow?.country_code || 'VN',
        language,
        fallbackLabel: pickedCityLabel,
      }));
    const settingResult = await persistSetting({ km, slot: 0, locationId });
    setSaving(false);
    if (settingResult.ok) {
      showToast(t('location.saved'));
      handleClose();
    } else if (!settingResult.skipped) {
      showToast(t('location.settingFailedDetail', { detail: settingResult.message || 'radius' }));
    } else {
      showToast(t('location.saved'));
      handleClose();
    }
  }, [
    clearGpsUnmapped,
    gpsManualStep,
    handleClose,
    language,
    manualCity,
    manualCountry,
    persistSetting,
    pickSlotLabel,
    primaryRadiusKm,
    saving,
    secondaryDisplayLabel,
    selectedDistrictRow,
    setActiveSource,
    setGpsSlot,
    setSearchRadius,
    showToast,
    syncSelectedRegions,
    t,
  ]);

  const handleConfirmSecondary = useCallback(async () => {
    if (secondaryStep !== 'radius' || saving) return;
    const label = secondaryDisplayLabel || (selectedDistrictRow && formatDistrictResultLabel(selectedDistrictRow, language, 'city'));
    const cityEn = manualCity || selectedDistrictRow?.city_en;
    if (!label || !cityEn) {
      showToast(t('location.settingFailedDetail', { detail: 'city' }));
      return;
    }

    setSaving(true);
    const km = secondaryRadiusKm;
    setSearchRadius(km);

    const pickedCityLabel =
      selectedDistrictRow?.city_ko ||
      selectedDistrictRow?.city_en ||
      label.split(' · ').pop()?.trim() ||
      label;

    const pickValue = await locationValueFromDistrictPick(selectedDistrictRow, pickedCityLabel, {
      radius_km: km,
      country_code: manualCountry || selectedDistrictRow?.country_code,
    });
    if (!pickValue) {
      setSaving(false);
      showToast(t('location.settingFailedDetail', { detail: 'resolve' }));
      return;
    }

    setPickSlot(pickValue, { label: pickedCityLabel });
    setActiveSource('pick');

    const syncRegions = [gpsSlotLabel, pickedCityLabel].filter(Boolean);
    const syncResult = await syncSelectedRegions(syncRegions);
    if (!syncResult?.ok) {
      setSaving(false);
      showToast(t('location.settingFailedDetail', { detail: syncResult?.message || 'sync' }));
      return;
    }

    // district(구/군)를 골랐으면 그 locations.id를 저장 — city 이름으로만 재조회하면
    // 항상 도시 단위로 되돌아가버려서 구/군 선택이 저장 단계에서 사라지던 문제 수정
    const locationId =
      pickValue.district_id ||
      pickValue.city_id ||
      (await resolveCityLocationId(cityEn, {
        countryCode: manualCountry || selectedDistrictRow?.country_code || 'VN',
        language,
        fallbackLabel: pickedCityLabel,
      }));
    const settingResult = await persistSetting({
      km,
      slot: 1,
      locationId,
      label: promptLabelOnConfirm
        ? confirmLabel.trim() || defaultLabel || secondaryDisplayLabel || t('location.secondaryRegion')
        : undefined,
      // "주소 설정" 화면(LocationAreaSettingsPage)에서 명시적으로 검색→선택→저장한
      // 경우에만 true — 다른 화면에서 지역바 슬롯2를 캐주얼하게 바꾸는 동작은 계속
      // 저장 목록에 안 뜨게 false로 남김.
      isUserSaved: Boolean(promptLabelOnConfirm),
    });
    setSaving(false);
    if (settingResult.ok) {
      showToast(t('location.saved'));
      handleClose();
    } else if (!settingResult.skipped) {
      showToast(t('location.settingFailedDetail', { detail: settingResult.message || 'radius' }));
    } else {
      showToast(t('location.saved'));
      handleClose();
    }
  }, [
    confirmLabel,
    defaultLabel,
    handleClose,
    language,
    manualCity,
    manualCountry,
    persistSetting,
    promptLabelOnConfirm,
    saving,
    secondaryDisplayLabel,
    secondaryRadiusKm,
    secondaryStep,
    selectedDistrictRow,
    gpsSlotLabel,
    setPickSlot,
    setActiveSource,
    setSearchRadius,
    syncSelectedRegions,
    showToast,
    t,
  ]);

  const selectDistrictResult = useCallback(
    (item) => {
      const { row, label } = item;
      if (writePickMode) {
        // row(locations 원본 행)를 함께 넘겨서, 호출측이 원하면 location_id를
        // FK로 저장할 수 있게 한다. 기존 콜백들은 두 번째 인자를 무시하므로 호환됨.
        onWritePick?.(label, row);
        handleClose();
        return;
      }
      setSelectedDistrictRow(row);
      setSecondaryDisplayLabel(label);
      setManualCountry(row.country_code || '');
      setManualCity(row.city_en || row.city_ko || '');
      if (promptLabelOnConfirm) {
        setConfirmLabel(String(defaultLabel || label || '').trim());
      }
      setSecondaryStep('radius');
    },
    [defaultLabel, handleClose, onWritePick, promptLabelOnConfirm, writePickMode],
  );

  if (!open) return null;
  const interestLabel = interestDisplayLabel;
  const selectedSummary = [gpsCityName, interestLabel].filter(Boolean).join(', ');

  const sheetTitle = writePickMode
    ? t(pickTitleKey)
    : isTrackGps
      ? t('location.primaryRegion')
      : t('location.interestLocation');

  const body = (
        <div className="mingo-location-sheet-panel__body" style={isPage ? { maxHeight: 'none', overflow: 'visible', padding: '0 16px 24px' } : SHEET_BODY_STYLE}>
          {isTrackGps ? (
            <section className="mingo-location-sheet-section">
              {isGpsManualPick ? (
                <>
                  <p className="mingo-location-sheet-section__label">{t('location.gpsManualPickHint')}</p>
                  {gpsManualStep === 'search' ? (
                    <DistrictSearchPanel
                      query={districtQuery}
                      onQueryChange={setDistrictQuery}
                      results={districtResults}
                      searching={districtSearching}
                      onSelect={selectGpsManualDistrict}
                      t={t}
                      language={language}
                      autoFocus
                    />
                  ) : (
                    <>
                      <button
                        type="button"
                        className="mingo-location-sheet-back"
                        onClick={() => {
                          setGpsManualStep('search');
                          setSelectedDistrictRow(null);
                          setSecondaryDisplayLabel('');
                        }}
                      >
                        {t('location.backToSearch')}
                      </button>
                      <SelectedRegionBlock
                        label={secondaryDisplayLabel}
                        countryCode={manualCountry || selectedDistrictRow?.country_code}
                      />
                      <RadiusDropdown
                        label={t('location.selectRadius')}
                        value={primaryRadiusKm}
                        options={SLOT_RADIUS_OPTIONS}
                        onChange={setPrimaryRadiusKm}
                      />
                      <ConfirmButton
                        label={t('location.saveButton')}
                        disabled={saving}
                        onClick={() => void handleConfirmGpsManual()}
                      />
                    </>
                  )}
                </>
              ) : (
                <>
                  <p className="mingo-location-sheet-section__label">{t('location.viewByCurrentGps')}</p>
                  <GpsReadonlyBlock cityName={gpsCityName} countryCode={gpsCountryCode} gpsHint={t('location.gpsBadge')} />
                  <RadiusDropdown
                    label={t('location.selectRadius')}
                    value={primaryRadiusKm}
                    options={SLOT_RADIUS_OPTIONS}
                    onChange={setPrimaryRadiusKm}
                  />
                  <ConfirmButton label={t('location.saveButton')} disabled={saving} onClick={() => void handleConfirmPrimary()} />
                </>
              )}
            </section>
          ) : null}

          {isTrackManual ? (
            <section className="mingo-location-sheet-section">
              {writePickMode ? (
                <>
                  {currentLocation ? (
                    <button
                      type="button"
                      className="mingo-location-sheet-field__select"
                      style={{ marginBottom: 12 }}
                      onClick={() => {
                        const gpsLabel = getLocationName(currentLocation, language);
                        if (gpsLabel) onWritePick?.(gpsLabel, currentLocation);
                        handleClose();
                      }}
                    >
                      <span aria-hidden="true">
                        <IconMapPin size={16} stroke={2} aria-hidden />
                      </span>
                      <span className="mingo-location-sheet-result__label">
                        {t('write.useGpsRegion')} — {gpsCityName}
                      </span>
                    </button>
                  ) : null}
                  <input
                    type="search"
                    className="mingo-location-sheet-search"
                    value={districtQuery}
                    onChange={(e) => setDistrictQuery(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.currentTarget.blur();
                      }
                    }}
                    placeholder={t('location.searchPlaceholder')}
                    autoFocus
                  />
                  <div style={RESULT_LIST_STYLE} role="listbox" aria-label={t('location.pickCityTitle')}>
                    {districtSearching ? (
                      <p style={{ margin: '8px 0', fontSize: 13, color: 'var(--mingo-text-secondary)' }}>{t('location.gpsDetecting')}</p>
                    ) : null}
                    {!districtSearching && districtQuery.trim() && districtResults.length === 0 ? (
                      <p style={{ margin: '8px 0', fontSize: 13, color: 'var(--mingo-text-secondary)' }}>{t('location.emptySelection')}</p>
                    ) : null}
                    {!districtSearching
                      ? districtResults.map((item) => (
                          <button
                            key={`${item.mode}-${item.row.id ?? item.label}`}
                            type="button"
                            className="mingo-location-sheet-field__select"
                            role="option"
                            aria-selected={false}
                            onClick={() => selectDistrictResult(item)}
                          >
                            <span className="mingo-location-sheet-result__flag" aria-hidden>
                              {flagEmojiFromCountryCode(item.row?.country_code)}
                            </span>
                            <span className="mingo-location-sheet-result__label">{item.label}</span>
                          </button>
                        ))
                      : null}
                  </div>
                </>
              ) : (
                <>
              {!isPage ? (
              <div style={{ marginBottom: 10 }}>
                <p style={{ margin: '0 0 8px', fontSize: 13, color: 'var(--mingo-text-secondary)' }}>
                  {t('location.currentSelection', { summary: selectedSummary || '-' })}
                </p>
                {gpsCityName || interestLabel ? (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                    {gpsCityName ? (
                      <span
                        key="gps-chip"
                        style={{
                          border: '1px solid var(--mingo-border)',
                          background: 'var(--mingo-bg-secondary)',
                          borderRadius: 999,
                          padding: '6px 10px',
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 6,
                          fontSize: 12,
                          color: 'var(--mingo-text)',
                        }}
                        title={t('location.gpsCityTitle')}
                      >
                        <span>{gpsCityName}</span>
                        <span style={{ fontSize: 11, color: 'var(--mingo-primary)' }}>{t('location.gpsBadge')}</span>
                      </span>
                    ) : null}
                    {interestLabel ? (
                      <button
                        key="interest-chip"
                        type="button"
                        onClick={() => {
                          setPickSlot(null);
                          const syncArr = gpsSlotLabel ? [gpsSlotLabel] : [];
                          void syncSelectedRegions(syncArr);
                        }}
                        style={{
                          border: '1px solid var(--mingo-border)',
                          background: 'var(--mingo-card)',
                          borderRadius: 999,
                          padding: '6px 10px',
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 6,
                          cursor: 'pointer',
                          fontSize: 12,
                          color: 'var(--mingo-text)',
                        }}
                        aria-label={t('location.removeInterest', { region: interestLabel })}
                        title={t('location.removeInterest', { region: interestLabel })}
                      >
                        <span>{interestLabel}</span>
                        <span aria-hidden="true">×</span>
                      </button>
                    ) : null}
                  </div>
                ) : null}
              </div>
              ) : null}
              {secondaryStep === 'search' ? (
                <DistrictSearchPanel
                  query={districtQuery}
                  onQueryChange={setDistrictQuery}
                  results={districtResults}
                  searching={districtSearching}
                  onSelect={selectDistrictResult}
                  t={t}
                  language={language}
                  autoFocus
                />
              ) : (
                <>
                  <button
                    type="button"
                    className="mingo-location-sheet-back"
                    onClick={() => {
                      setSecondaryStep('search');
                      setSelectedDistrictRow(null);
                      setSecondaryDisplayLabel('');
                      setConfirmLabel('');
                    }}
                  >
                    {t('location.searchAgain')}
                  </button>
                  {secondaryDisplayLabel ? (
                    <SelectedRegionBlock
                      label={secondaryDisplayLabel}
                      countryCode={selectedDistrictRow?.country_code}
                    />
                  ) : null}
                  <RadiusDropdown
                    label={t('location.selectRadius')}
                    value={secondaryRadiusKm}
                    options={SLOT_RADIUS_OPTIONS}
                    onChange={setSecondaryRadiusKm}
                  />
                  {promptLabelOnConfirm ? (
                    <label className="mingo-field" style={{ display: 'block', marginTop: 12 }}>
                      <span style={{ fontSize: 'var(--text-sm)', color: 'var(--mingo-text-secondary)' }}>
                        {t('location.areaLabelField')}
                      </span>
                      <input
                        className="mingo-input"
                        type="text"
                        value={confirmLabel}
                        onChange={(e) => setConfirmLabel(e.target.value)}
                        placeholder={
                          defaultLabel || secondaryDisplayLabel || t('location.areaLabelPlaceholder')
                        }
                        style={{ marginTop: 6 }}
                      />
                    </label>
                  ) : null}
                  <ConfirmButton
                    label={t('location.saveButton')}
                    disabled={saving || !secondaryDisplayLabel}
                    onClick={() => void handleConfirmSecondary()}
                  />
                </>
              )}
                </>
              )}
            </section>
          ) : null}
        </div>
  );

  if (isPage) {
    return (
      <div className="mingo-page mingo-page--stack" style={{ background: 'var(--mingo-bg)' }}>
        <PageHeaderWithBack
          title={t('location.areaSettingsTitle')}
          onBack={handleClose}
          rightSlot={pageHeaderRightSlot}
        />
        <RegionStateDebugBox active={open} />
        <div className="mingo-page__scroll">
          {aboveBody}
          {body}
          {belowBody}
        </div>
      </div>
    );
  }

  const sheet = (
    <div
      className={`mingo-location-sheet-root${entered ? ' mingo-location-sheet-root--open' : ''}`}
      role="presentation"
      onPointerUp={handleBackdropPointerUp}
    >
      <div
        className="mingo-location-sheet-panel"
        role="dialog"
        aria-modal="true"
        aria-label={sheetTitle}
        style={PANEL_STYLE}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mingo-location-sheet-panel__handle" aria-hidden="true" />
        <header className="mingo-location-sheet-panel__header">
          <h2 className="mingo-location-sheet-panel__title">{sheetTitle}</h2>
          <button type="button" className="mingo-location-sheet-panel__close" onClick={handleClose} aria-label={t('common.close')}>
            ×
          </button>
        </header>

        <RegionStateDebugBox active={open} />
        {body}
      </div>
    </div>
  );

  return createPortal(sheet, document.body);
}

/**
 * @param {{ label: string, countryCode?: string }} props
 */
function SelectedRegionBlock({ label, countryCode }) {
  const flag = flagEmojiForSelectedRegion(label, countryCode);
  return (
    <div className="mingo-location-sheet-selected" aria-readonly="true">
      <span className="mingo-location-sheet-selected__flag" aria-hidden="true">
        {flag}
      </span>
      <p className="mingo-location-sheet-selected__value">{label}</p>
    </div>
  );
}

/**
 * @param {{
 *   query: string;
 *   onQueryChange: (v: string) => void;
 *   results: Array<{ row: object; label: string; mode: string }>;
 *   searching: boolean;
 *   onSelect: (item: { row: object; label: string; mode?: string }) => void;
 *   t: (key: string, vars?: object) => string;
 *   language?: string;
 *   autoFocus?: boolean;
 * }} props
 */
function DistrictSearchPanel({
  query,
  onQueryChange,
  results,
  searching,
  onSelect,
  t,
  language = 'ko',
  autoFocus = false,
}) {
  const [mode, setMode] = useState('district');
  const [addressQuery, setAddressQuery] = useState('');
  const [addressBusy, setAddressBusy] = useState(false);
  const [addressError, setAddressError] = useState('');
  const [geoBusy, setGeoBusy] = useState(false);
  const [geoError, setGeoError] = useState('');

  async function runCurrentLocationSearch() {
    if (geoBusy) return;
    if (typeof navigator === 'undefined' || !navigator.geolocation) {
      setGeoError(t('foodOrder.checkout.geoUnsupported') || t('location.addressNotFound'));
      return;
    }
    setGeoBusy(true);
    setGeoError('');
    try {
      const pos = await new Promise((resolve, reject) =>
        navigator.geolocation.getCurrentPosition(resolve, reject, {
          enableHighAccuracy: true,
          timeout: 10000,
        }),
      );
      const { latitude: lat, longitude: lng } = pos.coords;
      const row = await findNearestDistrictRow(lat, lng);
      if (!row) {
        setGeoError(t('location.addressOutOfArea'));
        return;
      }
      const pickMode = isDistinctDistrict(row) ? 'district' : 'city';
      onSelect({ row, mode: pickMode, label: formatDistrictResultLabel(row, language, pickMode) });
    } catch {
      setGeoError(t('foodOrder.checkout.geoDenied') || t('location.addressNotFound'));
    } finally {
      setGeoBusy(false);
    }
  }

  async function runAddressSearch() {
    const q = String(addressQuery || '').trim();
    if (!q || addressBusy) return;
    setAddressBusy(true);
    setAddressError('');
    try {
      const hit = await geocodeAddress(q);
      if (!hit) {
        setAddressError(t('location.addressNotFound'));
        return;
      }
      const row = await findNearestDistrictRow(hit.lat, hit.lng);
      if (!row) {
        setAddressError(t('location.addressOutOfArea'));
        return;
      }
      const pickMode = isDistinctDistrict(row) ? 'district' : 'city';
      onSelect({
        row,
        mode: pickMode,
        label: formatDistrictResultLabel(row, language, pickMode),
      });
    } catch {
      setAddressError(t('location.addressNotFound'));
    } finally {
      setAddressBusy(false);
    }
  }

  return (
    <>
      <h3
        style={{
          margin: '4px 0 16px',
          fontSize: 22,
          fontWeight: 800,
          lineHeight: 1.3,
          color: 'var(--mingo-text)',
        }}
      >
        {t('location.areaSearchHeadline')}
      </h3>
      <div className="mingo-write__chips" style={{ marginBottom: 10 }}>
        <button
          type="button"
          className={`mingo-write__chip${mode === 'district' ? ' mingo-write__chip--active' : ''}`}
          onClick={() => {
            setMode('district');
            setAddressError('');
            setGeoError('');
          }}
        >
          {t('location.districtSearchTab')}
        </button>
        <button
          type="button"
          className={`mingo-write__chip${mode === 'address' ? ' mingo-write__chip--active' : ''}`}
          onClick={() => {
            setMode('address');
            setAddressError('');
            setGeoError('');
          }}
        >
          {t('location.addressSearchTab')}
        </button>
      </div>

      {mode === 'address' ? (
        <>
          <div style={{ position: 'relative', marginBottom: 12 }}>
            <span
              aria-hidden="true"
              style={{
                position: 'absolute',
                left: 14,
                top: '50%',
                transform: 'translateY(-50%)',
                color: 'var(--mingo-text-secondary)',
                fontSize: 14,
              }}
            >
              <IconSearch size={16} stroke={2} aria-hidden />
            </span>
            <input
              type="search"
              className="mingo-location-sheet-search"
              style={{ paddingLeft: 36, marginBottom: 0 }}
              value={addressQuery}
              onChange={(e) => setAddressQuery(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  void runAddressSearch();
                }
              }}
              placeholder={t('location.addressSearchPlaceholder')}
              autoFocus={autoFocus}
            />
          </div>
          <button
            type="button"
            onClick={() => void runCurrentLocationSearch()}
            disabled={geoBusy}
            style={{
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 8,
              padding: '13px 0',
              borderRadius: 10,
              border: '1px solid var(--mingo-border)',
              background: 'var(--mingo-card)',
              color: 'var(--mingo-text)',
              fontSize: 'var(--text-sm)',
              fontWeight: 600,
              cursor: geoBusy ? 'default' : 'pointer',
              marginTop: 12,
              marginBottom: 20,
            }}
          >
            <span aria-hidden="true">⊙</span>
            {geoBusy ? t('location.gpsDetecting') : t('location.useCurrentLocationBtn')}
          </button>

          {addressBusy ? (
            <p style={{ margin: '8px 0', fontSize: 'var(--text-sm)', color: 'var(--mingo-text-secondary)' }}>
              {t('location.addressSearching')}
            </p>
          ) : null}
          {addressError ? (
            <p style={{ margin: '8px 0', fontSize: 'var(--text-sm)', color: 'var(--mingo-error)' }}>
              {addressError}
            </p>
          ) : null}
          {geoError ? (
            <p style={{ margin: '8px 0', fontSize: 'var(--text-sm)', color: 'var(--mingo-error)' }}>{geoError}</p>
          ) : null}

          {!addressQuery.trim() ? (
            <div style={{ marginTop: 8 }}>
              <p
                style={{
                  margin: '0 0 8px',
                  fontSize: 'var(--text-sm)',
                  fontWeight: 700,
                  color: 'var(--mingo-text)',
                }}
              >
                {t('location.addressSearchTipsTitle')}
              </p>
              <ul
                style={{
                  margin: 0,
                  paddingLeft: 18,
                  fontSize: 'var(--text-sm)',
                  color: 'var(--mingo-text-secondary)',
                  lineHeight: 1.9,
                }}
              >
                <li>{t('location.addressSearchTip1')}</li>
                <li>{t('location.addressSearchTip2')}</li>
                <li>{t('location.addressSearchTip3')}</li>
              </ul>
            </div>
          ) : null}
        </>
      ) : (
        <>
          <div style={{ position: 'relative', marginBottom: 10 }}>
            <span
              aria-hidden="true"
              style={{
                position: 'absolute',
                left: 14,
                top: '50%',
                transform: 'translateY(-50%)',
                color: 'var(--mingo-text-secondary)',
                fontSize: 14,
              }}
            >
              <IconSearch size={16} stroke={2} aria-hidden />
            </span>
            <input
              type="search"
              className="mingo-location-sheet-search"
              style={{ paddingLeft: 36 }}
              value={query}
              onChange={(e) => onQueryChange(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.currentTarget.blur();
                }
              }}
              placeholder={t('location.searchPlaceholder')}
              autoFocus={autoFocus}
            />
          </div>
          <button
            type="button"
            onClick={() => void runCurrentLocationSearch()}
            disabled={geoBusy}
            style={{
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 8,
              padding: '13px 0',
              borderRadius: 10,
              border: '1px solid var(--mingo-border)',
              background: 'var(--mingo-card)',
              color: 'var(--mingo-text)',
              fontSize: 'var(--text-sm)',
              fontWeight: 600,
              cursor: geoBusy ? 'default' : 'pointer',
              marginBottom: 12,
            }}
          >
            <span aria-hidden="true">⊙</span>
            {geoBusy ? t('location.gpsDetecting') : t('location.useCurrentLocationBtn')}
          </button>
          {geoError ? (
            <p style={{ margin: '0 0 8px', fontSize: 'var(--text-sm)', color: 'var(--mingo-error)' }}>{geoError}</p>
          ) : null}
          <div style={RESULT_LIST_STYLE} role="listbox" aria-label={t('location.pickCityTitle')}>
            {searching ? (
              <p style={{ margin: '8px 0', fontSize: 'var(--text-sm)', color: 'var(--mingo-text-secondary)' }}>
                {t('location.gpsDetecting')}
              </p>
            ) : null}
            {!searching && query.trim() && results.length === 0 ? (
              <p style={{ margin: '8px 0', fontSize: 'var(--text-sm)', color: 'var(--mingo-text-secondary)' }}>
                {t('location.emptySelection')}
              </p>
            ) : null}
            {!searching
              ? results.map((item) => (
                  <button
                    key={`${item.mode}-${item.row.id ?? item.label}`}
                    type="button"
                    className="mingo-location-sheet-field__select"
                    role="option"
                    aria-selected={false}
                    onClick={() => onSelect(item)}
                  >
                    <span className="mingo-location-sheet-result__flag" aria-hidden>
                      {flagEmojiFromCountryCode(item.row?.country_code)}
                    </span>
                    <span className="mingo-location-sheet-result__label">{item.label}</span>
                  </button>
                ))
              : null}
          </div>
        </>
      )}
    </>
  );
}

/**
 * @param {{ cityName: string, countryCode?: string|null, gpsHint?: string }} props
 */
function GpsReadonlyBlock({ cityName, countryCode, gpsHint = 'GPS' }) {
  const flag = flagEmojiFromCountryCode(countryCode);
  return (
    <div className="mingo-location-sheet-readonly" aria-readonly="true" style={{ pointerEvents: 'none' }}>
      <span className="mingo-location-sheet-readonly__flag" aria-hidden="true">
        {flag}
      </span>
      <div className="mingo-location-sheet-readonly__text">
        <p className="mingo-location-sheet-readonly__value">{cityName}</p>
        <p className="mingo-location-sheet-readonly__hint">{gpsHint}</p>
      </div>
    </div>
  );
}

/**
 * @param {{ label: string; value: number; options: { km: number; label: string; desc?: string }[]; onChange: (km: number) => void }} props
 */
function RadiusDropdown({ label, value, options, onChange }) {
  return (
    <div className="mingo-location-sheet-radius mingo-location-sheet-radius--dropdown">
      <p className="mingo-location-sheet-radius__label">{label}</p>
      <select
        className="mingo-location-sheet-radius__select"
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        aria-label={label}
      >
        {options.map((opt) => (
          <option key={opt.km} value={opt.km}>
            {opt.label}
          </option>
        ))}
      </select>
      <p className="mingo-location-sheet-radius__desc">{options.find((o) => o.km === value)?.desc ?? ''}</p>
    </div>
  );
}

/**
 * @param {{ label: string; disabled?: boolean; onClick: () => void }} props
 */
function ConfirmButton({ label, disabled, onClick }) {
  return (
    <button
      type="button"
      className="mingo-location-sheet-confirm-btn mingo-confirm-btn"
      disabled={disabled}
      onClick={onClick}
    >
      {label}
    </button>
  );
}

export default LocationBottomSheet;
